/**
 * `withRouter` — WLASNA PODKLADKA, bo router 6 ja usunal.
 *
 * DLACZEGO TEN PLIK ISTNIEJE
 * --------------------------
 * `react-router-dom` 5 wstrzykiwal komponentom trzy propsy — `history`,
 * `location` i `match` — przez funkcje `withRouter`. W wersji 6 tej funkcji
 * NIE MA WCALE: wszystko oddaje przez hooki (`useNavigate`, `useLocation`,
 * `useParams`).
 *
 * Problem: hooki nie dzialaja w komponentach klasowych, a w tym projekcie
 * **64 komponenty dotykajace routera to klasy**. Przepisanie ich na funkcyjne
 * byloby przepisaniem polowy aplikacji przy okazji aktualizacji biblioteki —
 * dokladnie tego unikalismy w etapie 6 przy antd (tam `<Form ref>` pozwolil
 * zostawic klasy klasami).
 *
 * Ta podkladka robi to samo co `withRouter` z piatki: jest komponentem
 * FUNKCYJNYM (wiec wolno jej uzywac hookow), a wynik podaje nizej jako
 * propsy. Opakowany komponent moze zostac klasa i nie wie, ze cokolwiek
 * sie zmienilo.
 *
 * CO DOKLADNIE JEST POTRZEBNE — ZMIERZONE, NIE ZGADNIETE
 * ------------------------------------------------------
 * Przeglad calego `src` przed napisaniem tego pliku:
 *
 *     props.location.state       72
 *     props.location.pathname    19
 *     props.match.params.id      40
 *     props.history.replace       4
 *     props.match.params          1
 *     props.match.path            1   <- jedyne, ktore trzeba bylo przepisac
 *
 * Czyli powierzchnia jest waska i w calosci da sie ja odtworzyc.
 *
 * `history` TO NASZ WLASNY OBIEKT, nie router
 * -------------------------------------------
 * Aplikacja ma jeden wspolny obiekt historii w `src/config/historia.ts`
 * i wola na nim `push` 52 razy, `replace` 6 razy, a takze `listen`
 * i `location.search` — w tym z SAG, czyli spoza Reacta. Dlatego podkladka
 * podaje wlasnie ten obiekt, a nie opakowanie na `useNavigate()`.
 * Dzieki temu zadne z tych 60 wywolan nie wymaga zmiany.
 *
 * Spiecie tego obiektu z routerem 6 robi `<RouterHistorii>` w `router.tsx`.
 *
 * `match.path` — ZASTEPNIK, NIE ODPOWIEDNIK
 * -----------------------------------------
 * W piatce `match.path` to WZORZEC trasy (`/workspace/:id`), a nie adres.
 * Router 6 nie daje wzorca komponentowi w ten sposob. Jedyne uzycie w tym
 * projekcie porownywalo go ze wzorcem, zeby odpowiedziec na pytanie
 * "czy jestem na ekranie szczegolow obszaru" — i zostalo przepisane na
 * sprawdzenie samego adresu. Zostawiam tu `path` rowne adresowi, zeby nie
 * bylo `undefined`, ale NIE JEST to wzorzec i nie nalezy go tak uzywac.
 */
import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import historia from '../config/historia';

export interface PropsyRoutera<Parametry = Record<string, string | undefined>> {
    history: typeof historia;
    location: ReturnType<typeof useLocation>;
    match: {
        params: Parametry;
        /** UWAGA: to ADRES, nie wzorzec trasy — patrz komentarz u gory. */
        path: string;
        url: string;
    };
    /** Dodatek, ktorego piatka nie miala. Dla nowego kodu lepszy niz `history`. */
    navigate: ReturnType<typeof useNavigate>;
}

export function withRouter<P extends Partial<PropsyRoutera>>(
    Komponent: React.ComponentType<P>,
): React.FC<Omit<P, keyof PropsyRoutera>> {
    const ZRouterem: React.FC<Omit<P, keyof PropsyRoutera>> = (props) => {
        const location = useLocation();
        const params = useParams();
        const navigate = useNavigate();

        const match = React.useMemo(
            () => ({ params, path: location.pathname, url: location.pathname }),
            [params, location.pathname],
        );

        return (
            <Komponent
                {...(props as P)}
                history={historia}
                location={location}
                match={match}
                navigate={navigate}
            />
        );
    };

    // Zeby w narzedziach deweloperskich Reacta bylo widac, co jest opakowane.
    ZRouterem.displayName = `withRouter(${Komponent.displayName || Komponent.name || 'Komponent'})`;

    return ZRouterem;
}

export default withRouter;
